import { useState } from "react";
import { useSearchParams, useNavigate } from "react-router";
import { useTranslation } from "react-i18next";
import Card from "../components/ui/Card";
import Input from "../components/ui/Input";
import Button from "../components/ui/Button";
import { env } from "../env/config";

function ResetPasswordPage() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const token = searchParams.get("token") || "";
  const username = searchParams.get("username") || "";

  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const onSubmit = async (event) => {
    event.preventDefault();
    setError("");

    if (password.length < 8) {
      setError(
        t("new-ui.reset-password.min-length", {
          defaultValue: "Password must be at least 8 characters."
        })
      );
      return;
    }

    if (password !== confirmPassword) {
      setError(
        t("new-ui.reset-password.mismatch", {
          defaultValue: "Passwords do not match."
        })
      );
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(
        `${env.serverUrl}/apps/${env.appId}/request_password_reset`,
        {
          method: "POST",
          headers: { "Content-Type": "application/x-www-form-urlencoded" },
          body: new URLSearchParams({
            token,
            username,
            new_password: password
          })
        }
      );

      if (response.ok || response.redirected) {
        navigate("/resetpassword/success");
      } else {
        const text = await response.text();
        setError(text || t("new-ui.reset-password.error", { defaultValue: "Could not reset password. Try again." }));
      }
    } catch (submitError) {
      console.error("Reset password error", submitError);
      setError(
        submitError?.message ||
          t("new-ui.reset-password.error", { defaultValue: "Could not reset password. Try again." })
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-app-paper px-4 py-10 md:px-8">
      <div className="mx-auto max-w-xl">
        <Card className="animate-fade-up">
          <p className="ui-kicker">
            {t("new-ui.reset-password.kicker", { defaultValue: "DigiCert account" })}
          </p>
          <h1 className="ui-title mt-2 text-xl">
            {t("new-ui.reset-password.title", { defaultValue: "Set a new password" })}
          </h1>
          <p className="ui-copy mt-3">
            {t("new-ui.reset-password.description", {
              defaultValue: "Enter your new password below."
            })}
          </p>

          <form className="mt-6 space-y-4" onSubmit={onSubmit}>
            <Input
              id="new-password"
              label={t("new-ui.reset-password.new-password", { defaultValue: "New password" })}
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
            <Input
              id="confirm-password"
              label={t("new-ui.reset-password.confirm-password", { defaultValue: "Confirm password" })}
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
            />

            {error ? <p className="text-sm text-app-danger">{error}</p> : null}

            <Button type="submit" disabled={loading}>
              {loading
                ? t("new-ui.reset-password.saving", { defaultValue: "Saving..." })
                : t("new-ui.reset-password.save", { defaultValue: "Save new password" })}
            </Button>
          </form>
        </Card>
      </div>
    </div>
  );
}

export default ResetPasswordPage;
